import { memo, useCallback, useEffect, useState } from 'react'
import styles from '../positions/index.module.scss'
import { Button, Modal } from 'antd'
import { LeagueType } from 'types/league-types'
import {
    createLeagueType,
    deleteLeagueType,
    editLeagueType,
    getLeagueTypeById,
    getLeagueTypes,
} from 'services/league-types'
import { LeagueTypesTable } from '../../components/league-types-table'
import { LeagueTypeForm } from '../../components/league-type-form'

const LeagueTypes = memo(() => {
    const [types, setTypes] = useState<LeagueType[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [type, setType] = useState<LeagueType>({} as LeagueType)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getLeagueTypes()
        }

        getAll()
            .then(data => {
                setTypes(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setType({} as LeagueType)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getLeagueTypeById(id)
                .then(item => {
                    setType(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteLeagueType(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: LeagueType) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (type.id) {
                editLeagueType({
                    ...submitValues,
                    id: type.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createLeagueType(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, type, toggleModal]
    )

    const editMode = !!type.id
    const modalTitle = `${editMode ? `Edit league type ${type.name}` : `Add new league type`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            League Types
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <LeagueTypesTable data={types} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <LeagueTypeForm leagueType={type} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})

export default LeagueTypes
